import { Link, useParams } from "react-router-dom";
import { applicationUsagePath, useApi } from "../api/client";
import type { ApplicationUsage } from "../api/types";
import { CostCell } from "../components/CostCell";
import { Bar, Empty, ErrorState, Loading } from "../components/States";
import { ApplicationHistory } from "../components/ApplicationHistory";
import { Tier2Verdict } from "../components/Tier2Verdict";
import { fmtCost, fmtTokens, humanise, isVerifiedFree } from "../lib/format";

/** Application-level rollup: the total, and each document's contribution to it. */
export function ApplicationDetail() {
  const { id = "" } = useParams();
  const { data, error, loading, reload } = useApi<ApplicationUsage>(
    id ? applicationUsagePath(id) : null
  );

  if (loading) return <div className="card"><Loading what="application" /></div>;
  if (error) return <div className="card"><ErrorState message={error} onRetry={reload} /></div>;
  if (!data) return null;

  const maxTokens = Math.max(1, ...data.by_document.map((d) => d.total_tokens));

  return (
    <>
      <p className="page-sub" style={{ marginBottom: 8 }}>
        <Link to="/applications">← Applications</Link>
      </p>

      {/* The heading names the business where it is known, and falls back to
          the generic word rather than to the id: a uuid as an <h1> is neither
          readable nor a useful browser-tab or screen-reader landmark. The id
          is on the line below either way. */}
      <h1 className="page-title">{data.business_name || "Application"}</h1>
      <p className="page-sub">
        <span className="mono">{data.application_id}</span>
        {" · "}
        <Link to={`/documents?application_id=${data.application_id}`}>
          view its documents in the list
        </Link>
      </p>

      <div className="tiles">
        <div className="tile">
          <div className="tile-label">Documents</div>
          <div className="tile-value">{fmtTokens(data.documents)}</div>
        </div>
        <div className="tile">
          <div className="tile-label">Total tokens</div>
          <div className="tile-value">{fmtTokens(data.total_tokens)}</div>
          <div className="tile-note">
            {fmtTokens(data.total_input_tokens)} in · {fmtTokens(data.total_output_tokens)} out
          </div>
        </div>
        <div className="tile">
          <div className="tile-label">Estimated cost</div>
          <div className="tile-value">{fmtCost(data.estimated_cost_usd)}</div>
          {!data.cost_complete ? <div className="tile-note">lower bound</div> : null}
        </div>
      </div>

      {data.note ? <div className="notice">{data.note}</div> : null}

      <ApplicationHistory id={data.application_id} />

      <Tier2Verdict applicationId={data.application_id} />

      <div className="card">
        <div className="card-head"><span className="card-title">Per document</span></div>
        {data.by_document.length === 0 ? (
          <Empty title="No documents in this application" />
        ) : (
          <div className="table-wrap">
            <table>
              <thead>
                <tr>
                  <th scope="col">Document</th>
                  <th scope="col">Type</th>
                  <th scope="col" className="num">Tokens</th>
                  <th scope="col" style={{ width: 140 }}>Share</th>
                  <th scope="col" className="num">Cost</th>
                </tr>
              </thead>
              <tbody>
                {data.by_document.map((d) => (
                  <tr key={d.document_id}>
                    <td>
                      <Link to={`/documents/${d.document_id}`}>
                        {d.original_filename ?? d.document_id.slice(0, 8)}
                      </Link>
                      {!d.usage_recorded ? (
                        <> <span className="pill pill-warn" title="No usage rows for this document; its cost is unknown, not zero.">unrecorded</span></>
                      ) : null}
                    </td>
                    <td className="nowrap">
                      {d.document_type ? humanise(d.document_type) : <span className="muted">untyped</span>}
                    </td>
                    <td className="num">{fmtTokens(d.total_tokens)}</td>
                    <td><Bar value={d.total_tokens} max={maxTokens} /></td>
                    <td className="num">
                      <CostCell usd={d.cost_usd} recorded={d.usage_recorded} />
                    </td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr>
                  <th scope="row" colSpan={2}>Total</th>
                  <td className="num"><strong>{fmtTokens(data.total_tokens)}</strong></td>
                  <td />
                  <td className="num"><strong>{fmtCost(data.estimated_cost_usd)}</strong></td>
                </tr>
              </tfoot>
            </table>
          </div>
        )}
      </div>

      <div className="card">
        <div className="card-head"><span className="card-title">Per stage, across the application</span></div>
        {data.by_stage.length === 0 ? (
          <Empty title="No usage recorded for this application" />
        ) : (
          <div className="table-wrap">
            <table>
              <thead>
                <tr>
                  <th scope="col">Stage</th>
                  <th scope="col">Service</th>
                  <th scope="col">Provider / model</th>
                  <th scope="col" className="num">Calls</th>
                  <th scope="col" className="num">Tokens</th>
                  <th scope="col" className="num">Cost</th>
                </tr>
              </thead>
              <tbody>
                {data.by_stage.map((s, i) => (
                  <tr key={`${s.service}-${s.stage}-${s.model ?? i}`}>
                    <td>
                      {humanise(s.stage)}
                      {isVerifiedFree(s) ? <> <span className="pill">free</span></> : null}
                    </td>
                    <td className="muted">{s.service}</td>
                    <td className="mono">
                      {s.provider ?? "—"}
                      {s.model ? <div className="muted">{s.model}</div> : null}
                    </td>
                    <td className="num">{fmtTokens(s.calls)}</td>
                    <td className="num">{fmtTokens(s.total_tokens)}</td>
                    <td className="num">
                      <CostCell usd={s.cost_usd} verifiedFree={isVerifiedFree(s)} />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </>
  );
}
